import parsePhoneNumber, { type CountryCode } from 'libphonenumber-js';
import 'libphonenumber-js/min';

let _defaultCountry: CountryCode = 'US';

export function configurePhoneUtils(country: CountryCode) {
  _defaultCountry = country;
}

// User input is biased towards numbers local to the configured country, but we
// want to give ourselves every chance of successfully parsing international
// numbers without forcing the user to type `+`. This tries assuming a local
// number for countryCode, and if that produces an invalid number, tries
// assuming a country code prefix is present.
function parsePhoneNumberForgivingly(
  phone: string | undefined | null,
  countryCode: CountryCode = _defaultCountry,
) {
  if (!phone) return undefined;

  // Assume a number local to countryCode, unless it already has a + prefix
  let number = parsePhoneNumber(phone, countryCode);

  // If it's not valid for the assumed country, try treating it as an international number with a country code prefix
  if (!number?.isValid()) {
    number = parsePhoneNumber('+' + phone, countryCode);
  }
  return number;
}

// Once the input is done, we can also check overall length for NANP numbers.
export function isValidInternationalPhoneNumber(
  phone: string | null | undefined,
  countryCode: CountryCode = _defaultCountry,
): boolean {
  return !!parsePhoneNumberForgivingly(phone, countryCode)?.isValid();
}

// Format a number as e.g. +44525285822, for Twilio
export function formatInternationalPhoneNumberE164(
  phone: string | undefined | null,
  countryCode: CountryCode = _defaultCountry,
): string | null {
  const number = parsePhoneNumberForgivingly(phone, countryCode);
  return number?.number as string;
}

export function formatInternationalPhoneNumberHuman(
  phone: string,
  countryCode: CountryCode = _defaultCountry,
): string {
  const number = parsePhoneNumberForgivingly(phone, countryCode);
  if (number) {
    return number.formatInternational();
  }
  // Internal demo services on sandbox and QA produce sample data that includes
  // invalid US phone numbers. They'll fall down this branch along with other invalid numbers
  if (phone.length == 11 && countryCode === 'US') {
    return `+${phone.substring(0, 1)} ${phone.substring(2, 4)} ${phone.substring(4, 7)} ${phone.substring(7)}`;
  }
  return phone;
}
